import React, { useEffect, useMemo, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { updateGig } from "../../../redux/actions";
import history from "../../../routes/history";
import { alertNotification } from "../../../components/TopNotification/alertNotification";
import { getRelativeTime, isRemoteJobLocation } from "../../../shared/helpers";
import { canUseSubscriptionFeatures } from "../../../shared/billing";
import bannerImage from "../../../assets/img/banners/city-view.jpg";
import "./ManageJobCard.css";
import ManageJobCardHero from "./ManageJobCardHero";
import ManageJobCardSummary from "./ManageJobCardSummary";
import ManageJobCardStat from "./ManageJobCardStat";
import { getImageByStateCode } from "../../CompanyProfile/components/cloudinary";

const getSkillChips = (skills = []) =>
  (Array.isArray(skills) ? skills : [])
    .slice()
    .sort((a, b) => (b?.SkillPercent || 0) - (a?.SkillPercent || 0))
    .slice(0, 3)
    .map((item) => item?.Skills?.SkillName)
    .filter(Boolean);

const getStatusTone = (job) => {
  if (job.isDeleted || job.indeleted) return "deleted";
  if (job.closed) return "closed";
  if (job.JobOrderStatusName === "Expired" || job.expired) return "expired";
  return "published";
};

const getStatusLabel = (job) => {
  if (job.isDeleted || job.indeleted) return "Deleted";
  if (job.closed) return "Closed";
  if (job.JobOrderStatusName === "Expired" || job.expired) return "Expired";
  return job.JobOrderStatusName || "Published";
};

export default function ManageJobCard({ job, onExpirySuccess }) {
  const dispatch = useDispatch();
  const [heroImageSrc, setHeroImageSrc] = useState(job.img || bannerImage);
  const [usedStateFallback, setUsedStateFallback] = useState(false);
  const [republishLoading, setRepublishLoading] = useState(false);

  const skillChips = useMemo(() => getSkillChips(job.Skills || job.JobOrders_Skills), [job]);
  const statusTone = getStatusTone(job);
  const statusLabel = getStatusLabel(job);
  const isExpired = (job.JobOrderStatusName === "Expired" || job.expired) && !job.isDeleted && !job.closed;
  const relativeTime = getRelativeTime(job.created);
  const isRemoteJob = job?.WorkplacePolicy === "fully-remote" || isRemoteJobLocation(job);
  const locationLabel = isRemoteJob ? "Remote" : job.location;
  const showInvitedAction = canUseSubscriptionFeatures();

  useEffect(() => {
    setHeroImageSrc(job.img || bannerImage);
    setUsedStateFallback(false);
  }, [job.img]);

  const openJobDetail = () => {
    history.push({
      pathname: `/jobdetail/${job?.id}`,
      state: {
        id: job?.id,
        img: job.img,
        title: job.title,
        facilityname: job.facilityname,
        budget: job.BudgetMin,
        screen: job.screen,
        Rate: job.Rate,
        indraft: job.indraft,
        indeleted: job.indeleted,
        gigId: job.gigId,
        workpolicy: job.WorkplacePolicy,
        isDeleted: job.isDeleted,
        expiryDate: job.expiryDate,
        VMS: job?.VMS,
      },
    });
  };

  const handleRepublish = () => {
    if (republishLoading) return;

    const extendedDate = new Date();
    extendedDate.setMonth(extendedDate.getMonth() + 1);
    setRepublishLoading(true);

    dispatch(
      updateGig({
        Location: job?.jobdetails?.[0]?.Location,
        ExpiryDate: extendedDate.toISOString(),
        JobOrderStatusName: "Published",
        id: job.id,
        onSuccess: () => {
          setRepublishLoading(false);
          alertNotification("success", "Job expiry extended successfully");
          onExpirySuccess?.();
        },
      })
    );
  };

  const openMatches = () => {
    if (!job.matchedbids) return;
    history.push({
      pathname: "/skillmatches",
      state: {
        id: job.id,
        title: job.title,
        jobOrderId: job?.JobOrderId,
        fromMatches: true,
        jobdetails: job.jobdetails,
      },
    });
  };

  const openPlacedBids = () => {
    if (!job.placedbids) return;
    history.push({
      pathname: "/conversations",
      state: {
        jobType: "bid",
        bidType: "placed",
        conversationStatus: "placed_bids",
        jobOrderId: job?.JobOrderId,
        openConversation: true,
      },
    });
  };

  const openReceivedBids = () => {
    if (!job.receivedbids) return;
    history.push({
      pathname: "/conversations",
      state: {
        jobOrderId: job?.JobOrderId,
        conversationStatus: "received_bids",
        jobType: "proposal",
        bidType: "received",
        openConversation: true,
      },
    });
  };

  const openInvited = () => {
    if (!job.candidates) return;
    history.push({
      pathname: "/manage-invites",
      state: {
        jobOrderId: job?.JobOrderId,
        id: job?.id,
        title: job?.title,
        fromManageJobs: true,
      },
    });
  };

  const handleHeroImageError = () => {
    if (!usedStateFallback && job?.RegionCode) {
      setHeroImageSrc(
        getImageByStateCode(job.RegionCode, "state", job?.employerName || job?.facilityname)
      );
      setUsedStateFallback(true);
      return;
    }

    setHeroImageSrc(bannerImage);
  };

  return (
    <>
      <article
        className={`manage-job-card ${job.isDeleted || job.closed ? "manage-job-card--muted" : ""}`}
        onClick={openJobDetail}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openJobDetail();
          }
        }}
      >
        <ManageJobCardHero
          imageSrc={heroImageSrc}
          title={job.title}
          statusLabel={statusLabel}
          statusTone={statusTone}
          onOpenJob={openJobDetail}
          onImageError={handleHeroImageError}
        />

        <div className="manage-job-card__content">
          <ManageJobCardSummary
            title={job.title}
            facilityName={job.facilityname}
            location={locationLabel}
            relativeTime={relativeTime}
            rating={job.rating}
            reviews={job.reviews}
            isPrivate={job.isPrivate}
            vms={job.VMS}
            skillChips={skillChips}
          />

          {isExpired ? (
            <div className="manage-job-card__republish">
              <button
                type="button"
                className="manage-job-card__republish-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRepublish();
                }}
                disabled={republishLoading}
              >
                {republishLoading ? (
                  <>
                    <LoadingOutlined className="manage-job-card__republish-loader" />
                    Republishing...
                  </>
                ) : (
                  "Republish job"
                )}
              </button>
            </div>
          ) : job.closed ? null : (
            <div className={`manage-job-card__footer ${showInvitedAction ? "" : "manage-job-card__footer--three"}`}>
              <ManageJobCardStat
                value={job.matchedbids || 0}
                label="Matches"
                onClick={openMatches}
                disabled={!job.matchedbids}
              />
              <ManageJobCardStat
                value={job.placedbids || 0}
                label="Bids Placed"
                onClick={openPlacedBids}
                disabled={!job.placedbids}
              />
              <ManageJobCardStat
                value={job.receivedbids || 0}
                label="Bids Received"
                onClick={openReceivedBids}
                disabled={!job.receivedbids}
              />
              {showInvitedAction && (
                <ManageJobCardStat
                  value={job.candidates || 0}
                  label="Invited"
                  onClick={openInvited}
                  disabled={!job.candidates}
                />
              )}
            </div>
          )}
        </div>
      </article>
    </>
  );
}
